import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import type { Settings } from 'types/settings';
import type { DocumentNode } from 'graphql';
import type {
  AllCuratedStrainsListsQuery,
  AromasSearchQueryVariables as StrainFilterVariables,
  CuratedStrainsListQuery,
  CuratedStrainsListQueryVariables,
  RangeInput,
  RootQueryToStrainConnectionWhereArgs,
  SearchStrainsQuery,
} from 'graphql/wp/types-and-hooks';
import type { LanguageCodeFilterEnum } from './settings';
import type { RelatedStrains, Strain } from 'types/strains';
import type {
  QueryError,
  QueryProps,
  SingleNodeData,
  SingleNodeQueryResult,
} from 'lib/api';
import {
  AllCuratedStrainsListsDocument,
  AromasSearchDocument,
  ConditionsSearchDocument,
  CuratedStrainsListDocument,
  OrderEnum,
  PostObjectsConnectionOrderbyEnum,
  RelationEnum,
  TaxQueryField,
  TaxQueryOperator,
  SearchStrainsDocument,
  TaxonomyEnum,
  FlavorsSearchDocument,
  PositiveEffectsSearchDocument,
  StrainTypesSearchDocument,
  TerpenesSearchDocument,
} from 'graphql/wp/types-and-hooks';
import { getLanguageCodeFilter, getSettings } from './settings';
import { addApolloState } from 'lib/apollo';
import { request } from 'lib/rest';
import camelcaseKeys from 'camelcase-keys';

export const STRAINS_PER_PAGE = 12;

export const STRAIN_TAXONOMIES = {
  aromas: TaxonomyEnum.Aroma,
  flavors: TaxonomyEnum.Flavor,
  positiveEffects: TaxonomyEnum.Positiveeffect,
  strainTypes: TaxonomyEnum.Straintype,
  terpenes: TaxonomyEnum.Terpene,
};

export const TCH_LEVELS = {
  none: [0, 0],
  low: [1, 10],
  medium: [11, 20],
  high: [21, 100],
};

export type ThcLevelKeys = keyof typeof TCH_LEVELS;

export type StrainsTaxonomyFilters = Partial<
  Record<keyof Omit<typeof STRAIN_TAXONOMIES, 'strainTypes'>, string[]>
> & {
  strainTypes?: string | string[];
};

export type StrainsFilters = StrainsTaxonomyFilters & {
  conditionsIds?: string[];
  curatedList?: string;
  firstLetter?: string;
  thcQuantity?: ThcLevelKeys[];
};

function getStrainThcLevelsRange({
  quantityStart,
  quantityEnd,
}: {
  quantityStart: number;
  quantityEnd?: number;
}): GetThcLevelsRange {
  const levels = [];

  for (const [key, value] of Object.entries(TCH_LEVELS)) {
    const min = value[0];
    const max = value[1];

    if (quantityStart >= min && quantityStart <= max) {
      levels.push(key);
    }

    if (quantityEnd && quantityEnd >= min && quantityEnd <= max) {
      levels.push(key);
    }
  }

  return getThcLevelsRange(levels);
}

export type GetStrainsQueryWhereParams = Omit<StrainsFilters, 'thcQuantity'> & {
  languageCode: LanguageCodeFilterEnum;
  offset?: number;
  perPage?: number;
  thcQuantity?: Partial<Pick<RangeInput, 'gte' | 'lte'>>;
};

export function getStrainsQueryWhere({
  conditionsIds,
  curatedList,
  firstLetter,
  languageCode,
  perPage = STRAINS_PER_PAGE,
  offset = 0,
  thcQuantity,
  ...taxonomies
}: GetStrainsQueryWhereParams): RootQueryToStrainConnectionWhereArgs {
  const taxArray = Object.keys(taxonomies).reduce((filters, key) => {
    const terms = taxonomies[key];

    if (terms) {
      filters.push({
        field: TaxQueryField.Slug,
        taxonomy: STRAIN_TAXONOMIES[key],
        terms,
        operator: TaxQueryOperator.In,
      });
    }

    return filters;
  }, []);

  if (curatedList) {
    taxArray.push({
      field: TaxQueryField.Slug,
      taxonomy: TaxonomyEnum.Curatedstrainslist,
      terms: curatedList,
      operator: TaxQueryOperator.In,
    });
  }

  return {
    language: languageCode,
    orderby: [
      { field: PostObjectsConnectionOrderbyEnum.Title, order: OrderEnum.Asc },
    ],
    offsetPagination: { offset, size: perPage },
    ...(firstLetter ? { titleStartsWith: firstLetter } : {}),
    ...(conditionsIds ? { conditionsIds } : {}),
    ...(thcQuantity ? { thcQuantity: thcQuantity as RangeInput } : {}),
    ...(taxArray.length > 0
      ? {
          taxQuery: {
            taxArray,
            relation: RelationEnum.And,
          },
        }
      : {}),
  } as RootQueryToStrainConnectionWhereArgs;
}

export type GetAllCuratedStrainsLists = {
  curatedLists: AllCuratedStrainsListsQuery['curatedStrainsLists']['nodes'];
};

export async function getAllCuratedStrainsLists({
  client,
}: {
  client: QueryProps['client'];
}): Promise<GetAllCuratedStrainsLists & QueryError> {
  const { data, error } = await client.query<AllCuratedStrainsListsQuery>({
    query: AllCuratedStrainsListsDocument,
  });

  return {
    error,
    curatedLists: data?.curatedStrainsLists?.nodes || [],
  };
}

export type GetArchiveStrains = {
  strains: SearchStrainsQuery['strains'] | null;
};

export async function getArchiveStrains({
  client,
  variables: { thcQuantity, ...variables },
}: {
  client: QueryProps['client'];
  variables: Omit<GetStrainsQueryWhereParams, 'thcQuantity'> &
    Pick<StrainsFilters, 'thcQuantity'>;
}): Promise<GetArchiveStrains & QueryError> {
  const { error, data } = await client.query<SearchStrainsQuery>({
    query: SearchStrainsDocument,
    variables: {
      where: getStrainsQueryWhere({
        ...variables,
        ...(thcQuantity?.length
          ? { thcQuantity: getThcLevelsRange(thcQuantity) }
          : {}),
      }),
    },
  });

  return {
    error,
    strains: data?.strains || null,
  };
}

export type GetCuratedStrainsListPage = SingleNodeData<
  CuratedStrainsListQuery & {
    strains: GetInitialStrainsData;
    settings: Settings;
  }
>;

export async function getCuratedStrainsListPage({
  client,
  locale,
  params,
}: QueryProps): Promise<
  GetStaticPropsResult<SingleNodeQueryResult<GetCuratedStrainsListPage>>
> {
  const slug = params.slug as string;

  try {
    const languageCode = getLanguageCodeFilter(locale);

    const [curatedStrainsData, settingsData, strains] = await Promise.all([
      client.query<CuratedStrainsListQuery, CuratedStrainsListQueryVariables>({
        query: CuratedStrainsListDocument,
        variables: {
          slug,
        },
      }),
      getSettings(locale),
      getInitialStrainsData({
        client,
        curatedList: slug,
        languageCode,
      }),
    ]);
    const { data, error } = curatedStrainsData;
    if (error) {
      throw error;
    }

    if (!data?.curatedStrainsList) {
      return {
        notFound: true,
      };
    }

    const itemLanguage = data?.curatedStrainsList?.language?.slug;

    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (itemLanguage !== locale) {
      return data?.curatedStrainsList?.uri
        ? {
            redirect: {
              permanent: true,
              destination: data.curatedStrainsList.uri,
            },
          }
        : {
            notFound: true,
          };
    }

    if ((strains as QueryError)?.error) {
      throw (strains as QueryError)?.error;
    }

    const translations = data?.curatedStrainsList?.translations;
    const pageTranslations = translations?.map(
      ({ uri, language: { locale, slug } }) => {
        return {
          uri,
          locale,
          slug,
        };
      },
    );

    return addApolloState<GetCuratedStrainsListPage>(client, {
      props: {
        settings: {
          ...settingsData,
          pageTranslations: pageTranslations || null,
        },
        ...data,
        isPreview: false,
        strains: strains as GetInitialStrainsData,
      },
    });
  } catch (error) {
    console.log(`error getting single strain ${slug}:`, JSON.stringify(error));

    throw error;
  }
}

export type GetInitialStrainsData = GetStrainsFiltersData &
  Pick<GetArchiveStrains, 'strains'> & {
    variables?: StrainsFilters;
  };

export async function getInitialStrainsData({
  client,
  curatedList = null,
  languageCode,
}: {
  client: QueryProps['client'];
} & Pick<GetStrainsQueryWhereParams, 'curatedList' | 'languageCode'>): Promise<
  GetInitialStrainsData | QueryError
> {
  const variables = {
    curatedList,
    languageCode,
  };

  const [archiveStainsData, filtersData] = await Promise.all([
    getArchiveStrains({
      client,
      variables,
    }),
    getStrainsFiltersData({
      client,
      languageCode,
    }),
  ]);
  const { error: errorStrains, strains } = archiveStainsData;
  if (errorStrains) {
    return { error: errorStrains };
  }

  if ((filtersData as QueryError)?.error) {
    return { error: (filtersData as QueryError).error };
  }

  return {
    ...filtersData,
    strains,
    variables,
  };
}

export type SingleStrain = {
  strain: Omit<Strain, 'translations'>;
  settings: Settings;
  relatedStrains: RelatedStrains[];
  isPreview: boolean;
};

export function getThcLevelParameters(
  thcQuantity?: Partial<Pick<RangeInput, 'gte' | 'lte'>>,
): string {
  const thcParams = '';

  if (thcQuantity?.gte) {
    thcParams.concat(`gte=${thcQuantity.gte}`);
  }

  if (thcQuantity?.lte) {
    thcParams.concat(`&let=${thcQuantity.lte}`);
  }

  return thcParams;
}

export async function getRelatedStrains({
  currentId,
  locale,
  strainTypes,
  thcQuantity,
}: {
  currentId?: number;
  locale?: string;
  strainTypes?: string | string[];
  thcQuantity?: Partial<Pick<RangeInput, 'gte' | 'lte'>>;
}): Promise<RelatedStrains[]> {
  const thcInputParameters = getThcLevelParameters(thcQuantity);
  const { error, data } = await request<RelatedStrains[]>(
    `/related-strains?strain_types=${strainTypes}&not_in=${currentId}&${thcInputParameters}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw { error };
  }

  return camelcaseKeys(data.items, { deep: true });
}

export async function getSingleStrainData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<Strain> {
  const { error, data } = await request<Strain>(
    `/strain?slug=${slug}&_fields=id,title,content,link,featured_media,negative_effect,positive_effect,aroma,flavor,strain_type,terpene,seo,acf,translations,language,type${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw { error };
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export async function getSingleStrain({
  locale,
  params,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<SingleStrain>> {
  const slug = Array.isArray(params.slug) ? params.slug.join('/') : params.slug;

  try {
    const [singleStrain, settings] = await Promise.all([
      getSingleStrainData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    if (!singleStrain || !settings) {
      return {
        notFound: true,
      };
    }

    const itemLanguage = singleStrain?.language;
    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (!preview && itemLanguage !== locale) {
      return singleStrain?.link
        ? {
            redirect: {
              permanent: true,
              destination: singleStrain.link,
            },
          }
        : {
            notFound: true,
          };
    }

    const relatedStrains = await getRelatedStrains({
      currentId: singleStrain.id,
      locale,
      strainTypes: singleStrain?.strainType?.map(({ slug }) => slug),
      thcQuantity: getStrainThcLevelsRange(singleStrain?.acf?.thc),
    });

    return {
      props: {
        settings: {
          ...settings,
          pageTranslations: singleStrain.translations,
        },
        strain: singleStrain,
        relatedStrains,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(
      `error getting single strain ${params?.slug}:`,
      JSON.stringify(error),
    );

    throw error;
  }
}

export function getStrainsFilterQuery(
  key: keyof StrainsFilters,
): DocumentNode | null {
  switch (key) {
    case 'aromas':
      return AromasSearchDocument;
    case 'conditionsIds':
      return ConditionsSearchDocument;
    case 'flavors':
      return FlavorsSearchDocument;
    case 'positiveEffects':
      return PositiveEffectsSearchDocument;
    case 'strainTypes':
      return StrainTypesSearchDocument;
    case 'terpenes':
      return TerpenesSearchDocument;
    default:
      return null;
  }
}

export type GetStrainsFilterData = {
  nodes: {
    name: string;
    slug: string;
  }[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
};

export async function getStrainsFilterData({
  client,
  key,
  variables,
}: Pick<QueryProps, 'client'> & {
  key: keyof StrainsFilters;
  variables: StrainFilterVariables;
}): Promise<GetStrainsFilterData & QueryError> {
  const query = getStrainsFilterQuery(key);

  if (!query) {
    return null;
  }

  const { data, error } = await client.query<GetStrainsFilterData>({
    query,
    variables,
  });

  return {
    error,
    nodes: data?.[key]?.nodes || [],
    pageInfo: data?.[key]?.pageInfo || { endCursor: null, hasNextPage: false },
  };
}

export type GetStrainsFiltersData = Record<
  keyof Omit<StrainsFilters, 'firstLetter' | 'thcQuantity'>,
  GetStrainsFilterData
> &
  Pick<StrainsFilters, 'thcQuantity'>;

export async function getStrainsFiltersData({
  client,
  languageCode,
}: Pick<QueryProps, 'client'> &
  Pick<GetStrainsQueryWhereParams, 'languageCode'>): Promise<
  GetStrainsFiltersData | QueryError
> {
  const filters = {} as GetStrainsFiltersData;
  const taxonomies = Object.keys(STRAIN_TAXONOMIES);

  for (const key of taxonomies) {
    const { error, ...data } = await getStrainsFilterData({
      client,
      key: key as keyof StrainsFilters,
      variables: { languageCode },
    });

    if (error) {
      return { error };
    }

    filters[key] = data;
  }

  const { error: conditionsError, ...conditionsIds } =
    await getStrainsFilterData({
      client,
      key: 'conditionsIds',
      variables: {
        languageCode,
      },
    });

  if (conditionsError) {
    return { error: conditionsError };
  }

  return {
    ...filters,
    conditionsIds,
    thcQuantity: Object.keys(TCH_LEVELS) as ThcLevelKeys[],
  };
}

export type GetThcLevelsRange =
  | GetStrainsQueryWhereParams['thcQuantity']
  | null;

export function getThcLevelsRange(
  thcLevels: ThcLevelKeys[],
): GetThcLevelsRange {
  if (thcLevels.length <= 0) {
    return null;
  }

  // Get range of thc quantities
  const min = TCH_LEVELS[thcLevels[0]][0];
  const max = TCH_LEVELS[thcLevels[thcLevels.length - 1]][1];

  const quantities: GetStrainsQueryWhereParams['thcQuantity'] = {};

  if (min > 0) {
    quantities.gte = min;
  }

  if (max < 100) {
    quantities.lte = max;
  }

  return quantities;
}
